import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../components/PageHeader.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { Modal } from '../components/Modal.js';
import { Field } from '../components/Field.js';
import {
  fetchAuditEvents,
  type AuditCategory,
  type AuditEvent,
} from '../api/endpoints.js';

const CATEGORIES: AuditCategory[] = [
  'identity',
  'provisioning',
  'purchase',
  'entitlement',
  'scim',
  'microsoft_consent',
  'email_send',
  'authorization',
  'admin',
];

export function AuditPage() {
  const [category, setCategory] = useState<AuditCategory | ''>('');
  const [actorEmail, setActorEmail] = useState('');
  const [selected, setSelected] = useState<AuditEvent | null>(null);

  const events = useQuery({
    queryKey: ['audit', category, actorEmail],
    queryFn: () =>
      fetchAuditEvents({
        category: category || undefined,
        actorEmail: actorEmail || undefined,
        limit: 200,
      }),
  });

  const columns: Column<AuditEvent>[] = [
    {
      key: 'time',
      header: 'When',
      render: (e) => new Date(e.occurred_at).toLocaleString(),
    },
    {
      key: 'category',
      header: 'Category',
      render: (e) => <span className="badge">{e.event_category}</span>,
    },
    { key: 'type', header: 'Event', render: (e) => e.event_type },
    { key: 'actor', header: 'Actor', render: (e) => e.actor_email ?? '—' },
    { key: 'target', header: 'Target org', render: (e) => e.target_org_id ?? '—' },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Audit Explorer"
        subtitle="Identity, provisioning, purchase, SCIM, consent, and admin events."
      />

      <div className="filter-row">
        <Field label="Category">
          <select
            className="field__select"
            value={category}
            onChange={(e) => setCategory(e.target.value as AuditCategory | '')}
          >
            <option value="">All categories</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Actor email">
          <input
            className="field__input"
            value={actorEmail}
            placeholder="filter by actor…"
            onChange={(e) => setActorEmail(e.target.value)}
          />
        </Field>
      </div>

      <DataTable
        columns={columns}
        rows={events.data}
        rowKey={(e) => e.id}
        isLoading={events.isLoading}
        isError={events.isError}
        error={events.error}
        onRetry={() => void events.refetch()}
        onRowClick={(e) => setSelected(e)}
        emptyMessage="No audit events match these filters."
      />

      <Modal
        open={Boolean(selected)}
        title={selected ? `${selected.event_category} · ${selected.event_type}` : ''}
        onClose={() => setSelected(null)}
      >
        {selected && (
          <>
            <dl className="kv">
              <dt>Occurred</dt>
              <dd>{new Date(selected.occurred_at).toLocaleString()}</dd>
              <dt>Actor</dt>
              <dd>{selected.actor_email ?? '—'}</dd>
              <dt>Actor org</dt>
              <dd>{selected.actor_org_id ?? '—'}</dd>
              <dt>Target org</dt>
              <dd>{selected.target_org_id ?? '—'}</dd>
              <dt>Target user</dt>
              <dd>{selected.target_user_zitadel_id ?? '—'}</dd>
            </dl>
            <h4 className="card__title" style={{ marginTop: 16 }}>
              Payload
            </h4>
            <pre className="code-block">{JSON.stringify(selected.payload, null, 2)}</pre>
          </>
        )}
      </Modal>
    </div>
  );
}
